"use client";

import { cn } from "@repo/ui/lib/utils";
import { AnimatePresence, motion, type Variants } from "motion/react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type React from "react";
import { useContext, useRef } from "react";
import { VscChromeClose as CloseIcon } from "react-icons/vsc";
import useSound from "use-sound";
import ClientOnlyPortal from "@/components/layout/ClientOnlyPortal";
import { ScrollLockContext } from "@/providers/scroll-lock-provider";
import { useStore } from "@/store";

type MenuItemProps = {
  isActive: boolean;
  label: string;
  onClick?: () => void;
};

const menuButtonVariants: Variants = {
  close: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.5,
      ease: "easeIn",
    },
  },
  open: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeIn",
    },
  },
};

export function MenuItem({ isActive, label, onClick }: Readonly<MenuItemProps>): React.ReactNode {
  const [playHover] = useSound("/audio/public_audio_menu-change.mp3", {
    volume: 0.5,
  });
  const [playSelected] = useSound("/audio/public_audio_menu-selected.mp3", {
    volume: 0.5,
  });

  return (
    <motion.button
      variants={menuButtonVariants}
      onMouseEnter={() => playHover()}
      type="button"
      onClick={() => {
        playSelected();
        onClick?.();
      }}
      className={cn(
        "hover:text-secondary-base hover:outline-none focus:text-secondary-base focus:outline-none",
        isActive && "!text-primary",
      )}
    >
      {label}
    </motion.button>
  );
}

const menuList = [
  { label: "HOME", pathname: "/" },
  { label: "BLOGS", pathname: "/blogs" },
  { label: "LAB", pathname: "/lab" },
  { label: "STUDIO", pathname: "/studio" },
];

const menuContainerVariants: Variants = {
  close: {
    opacity: 0,
    transition: {
      duration: 0.5,
      ease: "easeIn",
    },
  },
  open: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeIn",
      delayChildren: 0.5,
    },
  },
};

const closeButtonWrapperVariants: Variants = {
  close: {
    opacity: 0,
    rotate: 180,
  },
  open: {
    opacity: 1,
    rotate: 0,
  },
};

const menuContainerAnimation: Variants = {
  close: {
    opacity: 0,
    transition: {
      staggerChildren: 0.2,
    },
  },
  open: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

function Menu({
  className,
}: Readonly<{
  className?: string;
}>): React.ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const currentPathname = usePathname();
  const isOpen = useStore((state) => state.isMenuOpen);
  const setMenuOpen = useStore((state) => state.setIsMenuOpen);

  const { lockScroll, unlockScroll } = useContext(ScrollLockContext);

  const [playBackground, { stop: stopBackground }] = useSound("/audio/public_audio_menu-loop.mp3", {
    volume: 0.5,
    interrupt: true,
    loop: true,
  });

  return (
    <ClientOnlyPortal ref={ref} selector="body">
      <AnimatePresence
        onExitComplete={() => {
          unlockScroll("menu", 1);
          stopBackground();
        }}
      >
        {isOpen && (
          <motion.div
            onAnimationStart={(animationName) => {
              if (animationName === "open") {
                lockScroll("menu", 1);
                playBackground();
              }
            }}
            initial="close"
            animate="open"
            exit={{
              ...menuContainerVariants.close,
            }}
            className={cn("fixed inset-0 flex flex-col", className)}
            style={{
              backgroundImage: "url('/image/menu-background.gif')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            variants={menuContainerVariants}
          >
            <div className="flex justify-end items-center h-(--header-height) px-[20px]">
              <motion.div
                variants={closeButtonWrapperVariants}
                exit={{
                  ...closeButtonWrapperVariants.close,
                }}
              >
                <CloseIcon
                  style={{
                    cursor: "pointer",
                  }}
                  size={32}
                  onClick={() => setMenuOpen(false)}
                />
              </motion.div>
            </div>
            <div className="grid grid-cols-3 flex-1">
              <motion.div
                variants={menuContainerAnimation}
                exit={{
                  ...menuContainerAnimation.close,
                }}
                className="flex flex-col items-center justify-center text-7xl font-header space-y-10 col-start-1 col-span-3 desktop:col-span-1 desktop:col-start-3 bg-black/50  backdrop-blur-sm rounded-lg overflow-hidden desktop:m-10 m-4"
              >
                {menuList.map(({ label, pathname }) => (
                  <Link key={label} href={pathname}>
                    <MenuItem
                      onClick={() => setMenuOpen(false)}
                      label={label}
                      isActive={pathname === currentPathname}
                    />
                  </Link>
                ))}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </ClientOnlyPortal>
  );
}

export default Menu;
